/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
import { useHookstate } from '@hookstate/core'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useLang } from '~/hooks/useLang'
import notify from '~/utils/notify'
import type {
  DisabledNextButton,
  UserListItem,
} from '../interfaces/IWarehouseForm'
import { updateWarehouse, getWarehouseById } from '../services'
import IAgency from '~/features/Agency/interfaces/IAgency'

const disabledNextButtonDefault: DisabledNextButton = {
  disabledBasicInfo: true,
  disabledManagers: false,
  disabledOperators: false,
}

export function useWarehouseEditForm() {
  const { translate } = useLang()
  const history = useHistory()

  const warehouseId = useHookstate<number | null>(null)
  const name = useHookstate<string>('')
  const agency = useHookstate<IAgency | null>(null)
  const [managersList, setManagersList] = useState<readonly UserListItem[]>([])
  const [operatorsList, setOperatorsList] = useState<readonly UserListItem[]>([])
  const activeStep = useHookstate<number>(0)
  const disabledNextButton = useHookstate<DisabledNextButton>(
    disabledNextButtonDefault
  )
  const isSubmitting = useHookstate<boolean>(false)
  const isLoading = useHookstate<boolean>(false)

  const handleCancelButton = () => {
    history.push('/warehouse')
  }

  const handleNext = () => {
    if (activeStep.get() < 3) {
      activeStep.set((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (activeStep.get() > 0) {
      activeStep.set((prev) => prev - 1)
    }
  }

  const loadWarehouse = async (id: number) => {
    try {
      isLoading.set(true)
      const warehouse = await getWarehouseById(id)

      warehouseId.set(warehouse.id)
      name.set(warehouse.name)
      agency.set(warehouse.agency || null)

      // Processar warehouse_users array e separar entre managers e operators
      const managers: UserListItem[] = []
      const operators: UserListItem[] = []

      if (
        warehouse.warehouse_users &&
        Array.isArray(warehouse.warehouse_users)
      ) {
        warehouse.warehouse_users.forEach((warehouseUser: any) => {
          const userData = warehouseUser.user

          if (!userData?.id) {
            return
          }

          const userInfo: UserListItem = {
            id: userData.id,
            name: userData.name || userData.representation_name || '',
            representation_name: userData.representation_name || '',
            registration: userData.registration ?? undefined,
            email: userData.email ?? undefined,
          }

          if (
            warehouseUser.is_manager === 1 ||
            warehouseUser.is_manager === true
          ) {
            managers.push(userInfo)
          }

          if (
            warehouseUser.is_operator === 1 ||
            warehouseUser.is_operator === true
          ) {
            operators.push(userInfo)
          }
        })
      }

      // Sempre setar as listas, mesmo que vazias
      setManagersList(managers)
      setOperatorsList(operators)
    } catch (error: any) {
      notify({
        message: error?.message || translate('Error loading warehouse'),
        type: 'error',
      })
      history.push('/warehouse')
    } finally {
      isLoading.set(false)
    }
  }

  const addManager = (user: any): boolean => {
    const userAlreadyExists = managersList.some((manager) => manager.id === user.id)

    if (userAlreadyExists) {
      notify({
        message: translate('User already added to managers list'),
        type: 'warning',
      })
      return false
    }

    const operatorExists = operatorsList.some((operator) => operator.id === user.id)
    if (operatorExists) {
      notify({
        message: translate('User is already in operators list'),
        type: 'warning',
      })
      return false
    }

    setManagersList([...managersList, {
      id: user.id,
      name: user.name || user.representation_name || '',
      representation_name: user.representation_name || '',
      registration: user.registration || undefined,
      email: user.email || undefined,
    }])

    return true
  }

  const removeManager = (userId: number) => {
    const updatedList = managersList.filter((manager) => manager.id !== userId);
    setManagersList(updatedList);
  }

  const addOperator = (user: any): boolean => {
    const userAlreadyExists = operatorsList.some((operator) => operator.id === user.id)

    if (userAlreadyExists) {
      notify({
        message: translate('User already added to operators list'),
        type: 'warning',
      })
      return false
    }

    const managerExists = managersList.some((manager) => manager.id === user.id)
    if (managerExists) {
      notify({
        message: translate('User is already in managers list'),
        type: 'warning',
      })
      return false
    }

    setOperatorsList([...operatorsList, {
      id: user.id,
      name: user.name || user.representation_name || '',
      registration: user.registration || undefined,
      representation_name: user.representation_name || '',
      email: user.email || undefined,
    }])

    return true
  }

  const removeOperator = (userId: number) => {
    const updatedList = operatorsList.filter((operator) => operator.id !== userId);
    setOperatorsList(updatedList);
  }

  const handleUpdateWarehouse = async () => {
    try {
      isSubmitting.set(true)

      const agencyData = agency.get()
      if (!agencyData?.id) {
        notify({
          message: translate('Agency is required'),
          type: 'error',
        })
        return
      }

      // Criar array de usuários com managers e operators
      const users = [
        ...managersList.map((manager) => ({
          user_id: manager.id,
          is_manager: true,
          is_operator: false,
        })),
        ...operatorsList.map((operator) => ({
          user_id: operator.id,
          is_manager: false,
          is_operator: true,
        })),
      ]

      const payload = {
        name: name.get(),
        agency_id: agencyData.id,
        users: users,
      }

      await updateWarehouse(warehouseId.get()!, payload)

      notify({
        message: translate('Warehouse successfully updated!'),
        type: 'success',
      })
      history.push('/warehouse')
    } catch (error: any) {
      notify({
        message: error?.message || translate('Error updating warehouse'),
        type: 'error',
      })
    } finally {
      isSubmitting.set(false)
    }
  }

  // Funções de validação
  const getBasicInfoValidity = () => {
    return Boolean(name.get().trim()) && Boolean(agency.get()?.id)
  }

  const getManagersValidity = () => {
    return managersList.length >= 1
  }

  return {
    warehouseId,
    name,
    agency,
    managersList,
    operatorsList,
    activeStep,
    disabledNextButton,
    isSubmitting,
    isLoading,
    // functions
    handleCancelButton,
    handleNext,
    handleBack,
    addManager,
    removeManager,
    addOperator,
    removeOperator,
    handleUpdateWarehouse,
    getBasicInfoValidity,
    getManagersValidity,
    loadWarehouse,
  }
}
